import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { Loader2 } from 'lucide-react'
import { getBillingProfile, updateBillingProfile, type BillingProfile } from '@/api/billing'
import { COUNTRIES } from '@/data/countries'

/**
 * Country and tax ID, in Settings → Account.
 *
 * The country is not cosmetic: it picks the checkout currency and the tax rule
 * on every invoice. The tax ID is snapshotted onto each invoice when it is
 * issued, so editing it later never rewrites an invoice already filed.
 */
export default function BillingProfileCard() {
  const [profile, setProfile] = useState<BillingProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    try {
      setProfile(await getBillingProfile())
    } catch {
      setProfile(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const save = async () => {
    if (!profile) return
    setSaving(true)
    try {
      setProfile(await updateBillingProfile(profile))
      toast.success('Billing details saved')
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(detail || 'Could not save the billing details')
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <Loader2 className="spin" size={16} />
  if (!profile) return null

  const isIndia = (profile.country || '').toUpperCase() === 'IN'

  return (
    <section style={{
      background: 'var(--color-bg-secondary)',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--radius-lg)',
      padding: '1.5rem',
      marginBottom: '1.5rem',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '0.25rem' }}>
        <span className="material-icons" style={{ fontSize: 21, color: 'var(--color-accent)' }}>receipt_long</span>
        <h2 style={{ fontSize: '1.125rem', fontWeight: 600, margin: 0 }}>Billing details</h2>
      </div>
      <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: '0 0 1.25rem 28px' }}>
        Used on your invoices and to determine your billing currency
        {profile.currency ? ` (currently ${profile.currency})` : ''}.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        <div>
          <label htmlFor="billing-country">Country</label>
          <select
            id="billing-country"
            value={profile.country || ''}
            onChange={(e) => setProfile({ ...profile, country: e.target.value || null })}
            style={{ width: '100%' }}
          >
            <option value="">Not set</option>
            {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="billing-taxid">{isIndia ? 'GSTIN' : 'Tax ID'}</label>
          <input
            id="billing-taxid"
            value={profile.tax_id || ''}
            onChange={(e) => setProfile({ ...profile, tax_id: e.target.value.toUpperCase() || null })}
            placeholder={isIndia ? '15-character GSTIN' : 'Local tax registration number'}
            style={{ width: '100%', fontFamily: 'monospace' }}
          />
          {isIndia && (
            <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 4 }}>
              A GSTIN is exactly 15 characters. Leave blank if you don't have one.
            </div>
          )}
        </div>
        <div>
          <label htmlFor="billing-name">Billing name</label>
          <input
            id="billing-name"
            value={profile.billing_name || ''}
            onChange={(e) => setProfile({ ...profile, billing_name: e.target.value || null })}
            placeholder="Name or company on the invoice"
            style={{ width: '100%' }}
          />
        </div>
        <div>
          <label htmlFor="billing-state">State / region</label>
          <input
            id="billing-state"
            value={profile.billing_state || ''}
            onChange={(e) => setProfile({ ...profile, billing_state: e.target.value || null })}
            style={{ width: '100%' }}
          />
        </div>
      </div>

      <button className="btn btn-primary" style={{ marginTop: '1.25rem' }} onClick={save} disabled={saving}>
        {saving ? <Loader2 size={14} className="spin" /> : 'Save billing details'}
      </button>
    </section>
  )
}
